import React, { useEffect, useState } from 'react';

import BaseInput, { BaseInputPropsWithoutChildren } from '../Input/BaseInput';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Dropdown from '../Dropdown/Dropdown';
import BodyText from '../Typography/BodyText/BodyText';
import Span from '../Element/Span/Span';
import Gutter from '../Gutter/Gutter';
import { CreateProps } from '../../types/utils/CreateProps';
import styles from './PhoneNumberInput.module.scss';
import InputMessage from '../Input/InputMessage/InputMessage';

export type countyCodeShape = {
  value: string;
  countryName: string;
  code: string;
};
// Alias for backwards compatibility
export type CountyCodeShape = countyCodeShape;

type PhoneNumberInputProps = CreateProps<
  {
    /** Label shown on the input */
    label: string;
    /** Callback which is triggered when user selects a countrycode from the list. The return value is the selected option as an object and native event */
    onCountryCodeChange: (
      event: React.SyntheticEvent<HTMLDivElement>,
      value: countyCodeShape | undefined
    ) => void;
    /** The selected countrycode options */
    selectedCountryCode?: countyCodeShape;
    /** Callback which gets event and pure value of typed number */
    onNumberChange: (
      event: React.ChangeEvent<HTMLInputElement>,
      option: string
    ) => void;
    /** Number shown inside number input. */
    numberValue?: string;
    /** Array of object with country names and codes */
    countryCodes: countyCodeShape[];
    /** Adds the plus sign to the input */
    addPlusSign?: boolean;
    /** Optional property to specify placeholder text displayed in the search input field */
    searchPlaceholder?: string;
  },
  BaseInputPropsWithoutChildren,
  'onChange' | 'value' | 'showCustomLabel' | 'type'
>;

const PhoneNumberInput: React.FunctionComponent<PhoneNumberInputProps> = ({
  onCountryCodeChange,
  selectedCountryCode,
  onNumberChange,
  numberValue,
  countryCodes,
  placeholder,
  disabled,
  errorText,
  helperText,
  label,
  required,
  addPlusSign,
  searchPlaceholder,
  ...props
}) => {
  const countryCodeWithSecondaryLabel = selectedCountryCode
    ? {
        ...selectedCountryCode,
        label: selectedCountryCode.countryName,
        secondaryLabel: `${'+'.concat(selectedCountryCode.code)}`,
      }
    : undefined;

  const [modifiedSelectedCountryCode, setModifiedSelection] = useState(
    countryCodeWithSecondaryLabel
  );

  const [originalOptions, setOriginalOptions] = useState(countryCodes);

  const modifiedCountryCodes = countryCodes.map((country) => ({
    value: country.value,
    label: addPlusSign
      ? `${country.countryName} +${country.code}`
      : `${country.countryName} ${country.code}`,
  }));

  if (modifiedCountryCodes.length > 1) {
    modifiedCountryCodes.sort((a, b) => a.label.localeCompare(b.label));
  }

  useEffect(() => {
    setOriginalOptions(countryCodes);
  }, [countryCodes]);

  useEffect(() => {
    setModifiedSelection(countryCodeWithSecondaryLabel);
  }, [selectedCountryCode]);

  useEffect(() => {
    if (selectedCountryCode) {
      setModifiedSelection(countryCodeWithSecondaryLabel);
    }
  }, []);

  const resolveLabel = required ? (
    <>
      <Span>{label} </Span>
      <Span className={styles.input_required}>*</Span>
    </>
  ) : (
    label
  );

  return (
    <Div className={styles.phoneNumberInput}>
      <BodyText
        tagName="span"
        size="sm"
        block
        color={disabled ? 'gray400' : 'black'}
      >
        {resolveLabel}
      </BodyText>
      <Gutter size="xs" />
      <Flex occupyVerticalSpace={false}>
        <Dropdown
          errorText={!!errorText ? ' ' : undefined}
          disabled={disabled}
          selectedOption={modifiedSelectedCountryCode}
          options={modifiedCountryCodes}
          onChange={(event, option) =>
            onCountryCodeChange(
              event,
              originalOptions.find(
                (originaloOption) => originaloOption.value === option.value
              )
            )
          }
          className={styles.countryCodeDropDown}
          showCustomLabel={true}
          searchPlaceholder={searchPlaceholder}
        ></Dropdown>
        <BaseInput
          {...props}
          iconName={!!errorText ? 'error-filled' : undefined}
          iconColor={!!errorText ? 'red600' : undefined}
          errorText={!!errorText ? ' ' : undefined}
          disabled={disabled}
          value={numberValue}
          className={styles.phoneNumberInputBox}
          onChange={onNumberChange}
          type="tel"
          placeholder={placeholder}
          showCustomLabel={true}
        />
      </Flex>
      {!!errorText && errorText !== ' ' && (
        <InputMessage text={errorText} color="red600" />
      )}
      {helperText ? <InputMessage text={helperText} color="gray500" /> : null}
    </Div>
  );
};

export default PhoneNumberInput;
